import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';
import {
    ProvisionSimulationFormDto,
    ProvisionSimulationRequestDto,
    ProvisionSimulationResultDto
} from '../../model/dto/api/provision-simulation.dto';

/**
 * Custom API service of the provision simulation — the two endpoints behind the dashboard navItem
 * "Simulation".
 *
 * Base URL `/mvsa/cp/provisionTypes`, the route the alias `cp.ProvisionType` already carries in
 * cp.entity-provider.ts. Shape follows the sibling read services of this module
 * ({@link CpProvisionAgentConfigurationService}, {@link CpProvisionAgentCoverageService}): an
 * `MvsCrudService` subclass whose only job is the custom endpoints of that route, while the generic
 * CRUD of the alias keeps going through the registry service.
 *
 * ── IT WRITES NOTHING, ALSO NOT ON THE SERVER
 *
 * `simulate` is a POST because it carries a body, not because it changes anything. The chain runs
 * with a transient run without an id, four guards hold it back from the four write sites, and a
 * fence refuses a simulation that somehow acquired one. Read permission is enough — nothing is
 * booked.
 *
 * ── EVERY BUSINESS OUTCOME IS HTTP 200
 *
 * `NOT_ELIGIBLE`, `NO_EVENT`, `CONFIGURATION_ERROR` and `ERROR` come back as a normal answer with a
 * full protocol; only a request that is not a question at all is a 400. Callers must therefore
 * branch on `ProvisionSimulationResultDto.state` inside `next`, and must NOT treat a non-COMPLETED
 * state as an error — routing it into the error handler throws away the very explanation the user
 * asked for.
 *
 * ── ENUMS GO OUT AS ORDINALS
 *
 * Measured 2026-09-02 against clone/rathje, both fields isolated separately: a request carrying
 * `"role": "main"` and one carrying `"paymentFrequency": "MONTHLY"` each answer HTTP 400 "Failed to
 * read request"; the same request with `0` for both answers 200. Callers assemble
 * {@link ProvisionSimulationRequestDto} with the ordinal.
 */
@Injectable({
    providedIn: 'root'
})
export class CpProvisionSimulationService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/cp/provisionTypes');
    }

    /**
     * GET /{id}/simulationForm — what the input form of this Provisionsart has to look like.
     *
     * The answer is DATA the form is built from, not a hint: `dimensions[].occupied` decides which
     * dimension is a visible field and which one goes into the collapsed group (Auflage D1), and
     * `blockingFinding` says up front that this Provisionsart can book nothing at all — before
     * anybody fills a form in.
     *
     * @param provisionTypeId id of the `cp.ProvisionType` to simulate
     */
    public getSimulationForm(provisionTypeId: number): Observable<ProvisionSimulationFormDto> {

        const url: string = `${this.apiUrl}/${provisionTypeId}/simulationForm`;

        return this.http.get<ProvisionSimulationFormDto>(url);
    }

    /**
     * POST /{id}/simulate — run the productive calculation chain over the given inputs and return
     * the result together with its protocol.
     *
     * @param provisionTypeId id of the `cp.ProvisionType` to simulate
     * @param request         mode A (dimensions, dates, premium, agents) or mode B (`contractId`)
     */
    public simulate(provisionTypeId: number,
                    request: ProvisionSimulationRequestDto): Observable<ProvisionSimulationResultDto> {

        const url: string = `${this.apiUrl}/${provisionTypeId}/simulate`;

        return this.http.post<ProvisionSimulationResultDto>(url, request);
    }

}
